import type {
  BatMetadataConfigEntry,
  FieldMetadataMessageDescriptorDef,
} from './common/types';
import { coreFields } from './common/coreFields';
import { vendorFields } from './common/vendorFields';
import { locationCountryField } from './common/locationFields';
import { msg } from '@lingui/core/macro';

const quantityRetiredKgco2e: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Quantity retired kgCO2e',
    context: 'Quantity retired within the start/end date range.',
  }),
  externalDescription: msg({
    message: 'Quantity retired within the start/end date range.',
    context: 'Quantity retired kgCO2e',
  }),
};

export const carbonCreditRetirementsMetadata = {
  displayName: msg({
    message: 'Carbon credit retirements',
    context: 'Activity data schema name for carbon credit retirements',
  }),
  fields: {
    ...coreFields,
    ...vendorFields,
    location_country: locationCountryField,
    carbon_removal_type: {
      displayName: msg({
        message: 'Carbon credit type',
        context: 'Category of carbon credit activity.',
      }),
      externalDescription: msg({
        message: 'Category of carbon credit activity.',
        context: 'Carbon credit type',
      }),
    },
    contract_execution_date: {
      displayName: msg({
        message: 'Contract execution date',
        context:
          'Effective date of contract. Should be the same for all rows sharing a contract unique ID.',
      }),
      externalDescription: msg({
        message:
          'Effective date of contract. Should be the same for all rows sharing a contract unique ID.',
        context: 'Contract execution date',
      }),
    },
    contract_id: {
      displayName: msg({
        message: 'Contract ID',
        context:
          'Unique identifier for the contract. Can be the internal or Salesforce ID or the name of the contract.',
      }),
      externalDescription: msg({
        message:
          'Unique identifier for the contract. Can be the internal or Salesforce ID or the name of the contract.',
        context: 'Contract ID',
      }),
    },
    contract_name: {
      displayName: msg({
        message: 'Contract name',
        context: 'Human-readable name for the contract.',
      }),
      externalDescription: msg({
        message: 'Human-readable name for the contract.',
        context: 'Contract name',
      }),
    },
    cost_per_kgco2e: {
      displayName: msg({
        message: 'Cost per kgCO2e',
        context: 'Cost per kgco2e of removal in the contract.',
      }),
      externalDescription: msg({
        message: 'Cost per kgco2e of removal in the contract.',
        context: 'Cost per kgCO2e',
      }),
    },
    cost_per_kgco2e_currency: {
      displayName: msg({
        message: 'Cost per kgCO2e currency',
        context: 'Currency used in "Cost per kgCO2e".',
      }),
      externalDescription: msg({
        message: 'Currency used in "Cost per kgCO2e".',
        context: 'Cost per kgCO2e currency',
      }),
    },
    is_3rd_party_verified: {
      displayName: msg({
        message: 'Is 3rd-party verified',
        context: 'Whether the removals are verified by a 3rd party.',
      }),
      externalDescription: msg({
        message: 'Whether the removals are verified by a 3rd party.',
        context: 'Is 3rd-party verified',
      }),
    },
    project_id: {
      displayName: msg({
        message: 'Project ID',
        context:
          'Unique identifier for the carbon removal project, usually referenced from the credit creator.',
      }),
      externalDescription: msg({
        message:
          'Unique identifier for the carbon removal project, usually referenced from the credit creator.',
        context: 'Project ID',
      }),
    },
    project_name: {
      displayName: msg({
        message: 'Project name',
        context:
          'Human-readable name for the carbon removal project (e.g. "Project Nexus").',
      }),
      externalDescription: msg({
        message:
          'Human-readable name for the carbon removal project (e.g. "Project Nexus").',
        context: 'Project name',
      }),
    },
    protocol: {
      displayName: msg({
        message: 'Protocol',
        context: 'Instruction set for which the project is answered.',
      }),
      externalDescription: msg({
        message: 'Instruction set for which the project is answered.',
        context: 'Protocol',
      }),
    },
    qualifies_as_corresponding_adjustment: {
      displayName: msg({
        message: 'Qualifies as corresponding adjustment',
        context: 'Whether the removal qualifies as a corresponding adjustment.',
      }),
      externalDescription: msg({
        message: 'Whether the removal qualifies as a corresponding adjustment.',
        context: 'Qualifies as corresponding adjustment',
      }),
    },
    quality_standard: {
      displayName: msg({
        message: 'Quality standard',
        context: 'Standard used to verify the removals.',
      }),
      externalDescription: msg({
        message: 'Standard used to verify the removals.',
        context: 'Quality standard',
      }),
    },
    quantity_contracted_kgco2e: {
      displayName: msg({
        message: 'Quantity contracted kgCO2e',
        context:
          'Total total removal (in kgCO2e) that this contract will deliver over its lifetime.',
      }),
      externalDescription: msg({
        message:
          'Total total removal (in kgCO2e) that this contract will deliver over its lifetime.',
        context: 'Quantity contracted kgCO2e',
      }),
    },
    quantity_retired_kgco2e: quantityRetiredKgco2e,
    // NOTE: quantity does not exist on the BAT but quantity_retired_kgco2e is
    // renamed to quantity in the BART-EM matching process
    quantity: quantityRetiredKgco2e,
    registry: {
      displayName: msg({
        message: 'Registry',
        context: 'Registry on which the removals are listed.',
      }),
      externalDescription: msg({
        message: 'Registry on which the removals are listed.',
        context: 'Registry',
      }),
    },
    retirement_date: {
      displayName: msg({
        message: 'Retirement date',
        context:
          'Date of retirement for credits in contract. If start and end date are not set, we use retirement_data and retirement_date + 1 day.',
      }),
      externalDescription: msg({
        message:
          'Date of retirement for credits in contract. If start and end date are not set, we use retirement_data and retirement_date + 1 day.',
        context: 'Retirement date',
      }),
    },
    serial_number: {
      displayName: msg({
        message: 'Credit serial number',
        context: 'Unique serial number of the credit being retired.',
      }),
      externalDescription: msg({
        message: 'Unique serial number of the credit being retired.',
        context: 'Credit serial number',
      }),
    },
  },
} as const satisfies BatMetadataConfigEntry;
